import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';
import { POLICY_RENEWAL_ITEMS, RENEWAL_DOCUMENT_AUXILIARY_DATA } from '../queries';

export const usePolicyRenewalUtils = () => {
  const client = useApolloClient();

  // Helpers
  const getItemData = useCallback(
    item => ({
      renewalDocumentId: item.documentId,
      documentNumber: item.renewalPolicyDocument.documentNumber,
      quotationsDate: item.renewalPolicyDocument.quotationsDate,
      sentToClientDate: item.renewalPolicyDocument.sentToClientDate,
      selected: false
    }),
    []
  );

  // Auxiliary Data
  const setAuxiliaryDataCache = (renewalDocumentId, additionalInfo) => {
    client.writeQuery({
      query: RENEWAL_DOCUMENT_AUXILIARY_DATA,
      data: {
        renewalDocumentAuxiliaryData: {
          __typename: 'RenewalDocumentAuxiliaryData',
          renewalDocumentId,
          additionalInfo
        }
      },
      variables: { renewalDocumentId }
    });
  };

  // #region Selection

  // Retrieve
  const retrieveAvailableItems = useCallback(() => {
    const { policyRenewalItems } = client.readQuery({ query: POLICY_RENEWAL_ITEMS });
    return [...(policyRenewalItems?.data || [])];
  }, [client]);

  const retrieveSelectedIds = useCallback(
    () =>
      retrieveAvailableItems()
        .filter(x => x.selected)
        .map(x => x.renewalDocumentId),
    [retrieveAvailableItems]
  );

  const retrieveSelectionSummary = useCallback(selections => {
    let disabledGenerateOffer = false,
      disabledRecomputeQuotations = false,
      disabledSentToClient = false,
      offerStatusSet = new Set() /* A set of booleans */,
      quotationsStatusSet = new Set(),
      sentToClientStatusSet = new Set();

    for (const item of selections) {
      const hasOffer = Boolean(item.documentNumber);
      const hasQuotations = Boolean(item.quotationsDate);
      const isSentToClient = Boolean(item.sentToClientDate);

      /* Actions */
      disabledGenerateOffer = disabledGenerateOffer || hasOffer; /* Can't generate offers if one policy has an offer */
      disabledRecomputeQuotations = disabledRecomputeQuotations || !hasOffer; /* Can't recompute quotations if one policy has no offer */
      disabledSentToClient =
        disabledSentToClient ||
        !hasOffer ||
        (hasOffer && isSentToClient); /* Can't send to client if one policy has no offer or is already sent to client */

      /* Warnings */
      offerStatusSet.add(hasOffer);
      quotationsStatusSet.add(hasQuotations);
      sentToClientStatusSet.add(isSentToClient);
    }

    const summary = {
      actions: {
        disabledGenerateOffer,
        disabledRecomputeQuotations,
        disabledSentToClient
      },
      warnings: {
        hasOfferWarning: offerStatusSet.size > 1 /* Contains (true, false) if some policies have an offer and some don't, etc... */,
        hasQuotationsWarning: quotationsStatusSet.size > 1,
        hasSentToClientWarning: sentToClientStatusSet.size > 1
      }
    };

    return summary;
  }, []);

  // Update
  const replaceAvailableItems = useCallback(
    data =>
      client.writeQuery({
        query: POLICY_RENEWAL_ITEMS,
        data: {
          policyRenewalItems: {
            __typename: 'policyRenewalItems',
            data
          }
        }
      }),
    [client]
  );

  const addAvailableItems = useCallback(
    items => {
      const availableItems = retrieveAvailableItems();

      // add if not exists
      items.forEach(item => {
        const itemData = getItemData(item);
        if (!availableItems.some(x => x.renewalDocumentId === itemData.renewalDocumentId)) availableItems.push(itemData);
      });

      client.writeQuery({
        query: POLICY_RENEWAL_ITEMS,
        data: {
          policyRenewalItems: {
            __typename: 'policyRenewalItems',
            data: availableItems
          }
        }
      });
    },
    [client, retrieveAvailableItems, getItemData]
  );

  const updateAvailableItem = useCallback(
    item => {
      let availableItems = retrieveAvailableItems();

      availableItems = availableItems.map(x => (x.renewalDocumentId === item.renewalDocumentId ? { ...x, ...item } : x));

      client.writeQuery({
        query: POLICY_RENEWAL_ITEMS,
        data: {
          policyRenewalItems: {
            __typename: 'policyRenewalItems',
            data: availableItems
          }
        }
      });
    },
    [client, retrieveAvailableItems]
  );

  const clearUnselectedItems = useCallback(() => {
    const availableItems = retrieveAvailableItems();
    client.writeQuery({
      query: POLICY_RENEWAL_ITEMS,
      data: {
        policyRenewalItems: {
          __typename: 'policyRenewalItems',
          data: availableItems.filter(x => x.selected)
        }
      }
    });
  }, [client, retrieveAvailableItems]);

  const deselectItems = useCallback(() => {
    const availableItems = retrieveAvailableItems();
    client.writeQuery({
      query: POLICY_RENEWAL_ITEMS,
      data: {
        policyRenewalItems: {
          __typename: 'policyRenewalItems',
          data: availableItems.map(x => ({ ...x, selected: false }))
        }
      }
    });
  }, [client, retrieveAvailableItems]);

  // Watch
  const watchSelections = useCallback(
    callback =>
      client
        .watchQuery({
          query: POLICY_RENEWAL_ITEMS
        })
        .subscribe({
          next: ({ data }) => {
            const items = data?.policyRenewalItems?.data || [];
            callback(items.filter(x => x.selected));
          }
        }),
    [client]
  );

  // #endregion

  return {
    setAuxiliaryDataCache,
    // Selection
    retrieveSelectedIds,
    retrieveAvailableItems,
    retrieveSelectionSummary,
    replaceAvailableItems,
    addAvailableItems,
    updateAvailableItem,
    clearUnselectedItems,
    deselectItems,
    watchSelections
  };
};
